import { Download, Loader2 } from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import { PaymentStatus } from '~entities/payment/constants';
import type { BirthdayStatusFilter } from '~entities/student/api/reporting-birthdays';
import { months } from '~entities/student/constants/monthsList';

import { ClassSelector } from '~features/payment/ui/class-selector';
import { PaymentStatusSelector } from '~features/payment/ui/payment-status-selector';
import { GroupSelector } from '~features/shared/group-selector';
import {
  type LocationOption,
  LocationSelector,
} from '~features/shared/location-selector';
import { SessionSelector } from '~features/shared/session-selector';

import {
  ALL_MONTHS,
  type MonthSelectorValue,
  type Report,
} from '~pages/payments/payment-reports/report-config';

import { DatePickerWithRange } from '~shared/components/date-picker-with-range';
import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

interface SessionOption {
  label: string;
  value: string;
}

interface ClassOption {
  label: string;
  value: string;
}

interface GroupOption {
  label: string;
  value: string;
}

interface ReportInputDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: Report | null;
  isLoading: boolean;
  onDownload: () => void;
  // Date range state
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  // Single date state
  singleDate: Date | undefined;
  onSingleDateChange: (date: Date | undefined) => void;
  // Session/Class state
  selectedSession: SessionOption | null;
  onSessionChange: (session: SessionOption | null) => void;
  selectedClass: ClassOption | null;
  onClassChange: (classOption: ClassOption | null) => void;
  selectedGroup: GroupOption | null;
  onGroupChange: (group: GroupOption | null) => void;
  // Location state
  selectedLocation: LocationOption | null;
  onLocationChange: (location: LocationOption | null) => void;
  // Payment status state
  paymentStatus: PaymentStatus | undefined;
  onPaymentStatusChange: (status: PaymentStatus | undefined) => void;
  // Days past due state
  daysPastDue: number | '';
  onDaysPastDueChange: (days: number | '') => void;
  // Stripe payment intents state
  stripePaymentIntents: number | string;
  onStripePaymentIntentsChange: (days: number | string) => void;
  // Transaction codes report lookback
  transactionCodesLookbackDays: number | string;
  onTransactionCodesLookbackDaysChange: (days: number | string) => void;
  // Month selector state (undefined when no month chosen yet)
  selectedMonth: MonthSelectorValue | undefined;
  onMonthChange: (month: MonthSelectorValue) => void;
  // Birthday report active/inactive status filter
  birthdayStatus: BirthdayStatusFilter;
  onBirthdayStatusChange: (status: BirthdayStatusFilter) => void;
  classKeywords: string;
  onClassKeywordsChange: (keywords: string) => void;
  // Optional age filters ("Age As Of" date + min/max age in years)
  ageAsOfDate: Date | undefined;
  onAgeAsOfDateChange: (date: Date | undefined) => void;
  studentMinAge: number | '';
  onStudentMinAgeChange: (age: number | '') => void;
  studentMaxAge: number | '';
  onStudentMaxAgeChange: (age: number | '') => void;
}

const birthdayStatusOptions: Array<{
  value: BirthdayStatusFilter;
  label: string;
}> = [
  { value: 'all', label: 'Both' },
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' },
];

const skipPaymentStatuses = [
  PaymentStatus.CANCELLED,
  PaymentStatus.VOID,
  PaymentStatus.PARTIALLY_PAID,
  PaymentStatus.FULL_REFUND,
  PaymentStatus.PARTIAL_REFUND,
  PaymentStatus.PENDING_REFUND,
  PaymentStatus.REFUND_FAILED,
];

export const ReportInputDialog = ({
  open,
  onOpenChange,
  report,
  isLoading,
  onDownload,
  dateRange,
  onDateRangeChange,
  singleDate,
  onSingleDateChange,
  selectedSession,
  onSessionChange,
  selectedClass,
  onClassChange,
  selectedGroup,
  onGroupChange,
  selectedLocation,
  onLocationChange,
  paymentStatus,
  onPaymentStatusChange,
  daysPastDue,
  onDaysPastDueChange,
  stripePaymentIntents,
  onStripePaymentIntentsChange,
  transactionCodesLookbackDays,
  onTransactionCodesLookbackDaysChange,
  selectedMonth,
  onMonthChange,
  birthdayStatus,
  onBirthdayStatusChange,
  classKeywords,
  onClassKeywordsChange,
  ageAsOfDate,
  onAgeAsOfDateChange,
  studentMinAge,
  onStudentMinAgeChange,
  studentMaxAge,
  onStudentMaxAgeChange,
}: ReportInputDialogProps) => {
  if (!report) return null;

  const isDownloadDisabled = () => {
    if (report.requiresGroupDialog) {
      if (!selectedSession?.value || !selectedGroup?.value) return true;
    }

    // Session/class validation
    if (
      report.requiresSessionDialog &&
      !report.requiresGroupDialog &&
      !report.allowSessionOrDateRange &&
      !selectedSession
    )
      return true;
    if (report.requiresClassDialog && (!selectedSession || !selectedClass))
      return true;

    if (report.allowSessionOrDateRange) {
      const hasSession = Boolean(selectedSession?.value);
      const hasDateRange = Boolean(dateRange?.from && dateRange.to);
      if (!hasSession && !hasDateRange) return true;
    }

    // Date range validation
    if (
      report.hasDateRange &&
      !report.allowSessionOrDateRange &&
      (!dateRange?.from || !dateRange.to)
    )
      return true;

    // Single date validation
    if (report.hasSingleDate && !singleDate) return true;

    // Location validation
    if (report.hasLocationSelector && !selectedLocation?.value) return true;

    // Days past due validation
    if (report.hasDaysPastDueInput && (daysPastDue === '' || daysPastDue <= 0))
      return true;

    // Stripe payment intents validation
    if (
      report.hasStripePaymentIntentsInput &&
      (stripePaymentIntents === '' || Number(stripePaymentIntents) <= 0)
    )
      return true;

    if (
      report.hasTransactionCodesLookbackInput &&
      (transactionCodesLookbackDays === '' ||
        Number(transactionCodesLookbackDays) <= 0)
    )
      return true;

    // Month validation
    if (report.hasMonthSelector && !selectedMonth) return true;

    // Age range validation
    if (
      report.hasStudentAgeFilters &&
      studentMinAge !== '' &&
      studentMaxAge !== '' &&
      studentMinAge > studentMaxAge
    )
      return true;

    return isLoading;
  };

  const showSessionSelector =
    report.requiresSessionDialog || report.requiresGroupDialog;
  const showGroupSelector = report.requiresGroupDialog;
  const showClassSelector =
    report.requiresClassDialog || report.hasOptionalClassFilter;
  const classRequired = report.requiresClassDialog;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-md overflow-visible'>
        <DialogHeader>
          <DialogTitle>{report.name}</DialogTitle>
          {report.description && (
            <DialogDescription>{report.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {/* Session Selector */}
          {showSessionSelector && (
            <div className='space-y-2'>
              <Label>Select Session</Label>
              <SessionSelector
                session={selectedSession}
                menuPortalTarget={null}
                onChange={(val) => {
                  onSessionChange(val ?? null);
                  if (showClassSelector) {
                    onClassChange(null);
                  }
                  if (showGroupSelector) {
                    onGroupChange(null);
                  }
                }}
              />
            </div>
          )}

          {showGroupSelector && (
            <div className='space-y-2'>
              <Label>Select Group</Label>
              <GroupSelector
                group={selectedGroup}
                onChange={onGroupChange}
                disabled={!selectedSession?.value}
                menuPortalTarget={null}
              />
            </div>
          )}

          {/* Class Selector */}
          {showClassSelector && (
            <div className='space-y-2'>
              <Label>
                {classRequired ? 'Select Class' : 'Select Class (Optional)'}
              </Label>
              <ClassSelector
                selectedSessionId={selectedSession?.value}
                selectedClass={selectedClass}
                menuPortalTarget={null}
                onChange={(val) => {
                  onClassChange(val ?? null);
                }}
              />
            </div>
          )}

          {/* Date Range Picker */}
          {report.hasDateRange && (
            <div className='space-y-2'>
              <Label>Select Date Range</Label>
              <DatePickerWithRange
                date={dateRange}
                setDate={onDateRangeChange}
                className='w-full'
                triggerClassName='w-full'
              />
            </div>
          )}

          {/* Single Date Picker */}
          {report.hasSingleDate && (
            <div className='space-y-2'>
              <Label>Select Date</Label>
              <DateTimePicker
                value={singleDate}
                onChange={onSingleDateChange}
                granularity='day'
                displayFormat={{ hour24: 'PPP' }}
              />
            </div>
          )}

          {/* Location Selector */}
          {report.hasLocationSelector && (
            <div className='space-y-2'>
              <Label>Select Location</Label>
              <LocationSelector
                value={selectedLocation}
                onChange={onLocationChange}
                menuPortalTarget={null}
              />
            </div>
          )}

          {/* Payment Status Selector */}
          {report.hasPaymentStatusSelector && (
            <div className='space-y-2'>
              <Label>Payment Status (Optional)</Label>
              <PaymentStatusSelector
                className='w-full'
                value={paymentStatus}
                skip={skipPaymentStatuses}
                onChange={onPaymentStatusChange}
              />
            </div>
          )}

          {/* Days Past Due Input */}
          {report.hasDaysPastDueInput && (
            <div className='space-y-2'>
              <Label>Days Past Due</Label>
              <Input
                type='number'
                min={1}
                placeholder='Enter number of days'
                value={daysPastDue}
                onChange={(e) => {
                  const val = e.target.value;
                  onDaysPastDueChange(
                    val === '' ? '' : Math.max(1, Number(val)),
                  );
                }}
              />
            </div>
          )}

          {/* Stripe Payment Intents Input */}
          {report.hasStripePaymentIntentsInput && (
            <div className='space-y-2'>
              <Label>Number of Days</Label>
              <Input
                type='number'
                min={1}
                placeholder='Enter number of days'
                value={stripePaymentIntents}
                onChange={(e) => {
                  const val = e.target.value;
                  onStripePaymentIntentsChange(
                    val === '' ? '' : Math.max(1, Number(val)),
                  );
                }}
              />
            </div>
          )}

          {report.hasTransactionCodesLookbackInput && (
            <div className='space-y-2'>
              <Label>Number of Days</Label>
              <Input
                type='number'
                min={1}
                placeholder='Enter number of days'
                value={transactionCodesLookbackDays}
                onChange={(e) => {
                  const val = e.target.value;
                  onTransactionCodesLookbackDaysChange(
                    val === '' ? '' : Math.max(1, Number(val)),
                  );
                }}
              />
            </div>
          )}

          {/* Month Selector */}
          {report.hasMonthSelector && (
            <div className='space-y-2'>
              <Label>Select Month</Label>
              <Select
                value={selectedMonth}
                onValueChange={(value) => {
                  onMonthChange(value as MonthSelectorValue);
                }}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder='Select Month' />
                </SelectTrigger>
                <SelectContent className='max-h-52'>
                  {report.hasAllMonthsOption && (
                    <SelectItem value={ALL_MONTHS}>All months</SelectItem>
                  )}
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Birthday Report Status Filter */}
          {report.hasBirthdayStatusFilter && (
            <div className='space-y-2'>
              <Label>Include Students</Label>
              <Select
                value={birthdayStatus}
                onValueChange={(value) => {
                  onBirthdayStatusChange(value as BirthdayStatusFilter);
                }}
              >
                <SelectTrigger className='w-full'>
                  <SelectValue placeholder='Include Students' />
                </SelectTrigger>
                <SelectContent>
                  {birthdayStatusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Optional age filters for student reports */}
          {report.hasStudentAgeFilters && (
            <div className='space-y-4'>
              <div className='space-y-2'>
                <Label>Age As Of (Optional)</Label>
                <div className='flex items-center gap-2'>
                  <div className='flex-1'>
                    <DateTimePicker
                      value={ageAsOfDate}
                      onChange={onAgeAsOfDateChange}
                      granularity='day'
                      displayFormat={{ hour24: 'PP', hour12: 'PP' }}
                      placeholder='Pick a date'
                    />
                  </div>
                  {ageAsOfDate && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={() => {
                        onAgeAsOfDateChange(undefined);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <p className='text-xs text-muted-foreground'>
                  Adds as-of age columns and evaluates the age range on that
                  date (e.g. who will be 10 by Nov 21).
                </p>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div className='space-y-2'>
                  <Label>Min Age (Optional)</Label>
                  <Input
                    type='number'
                    min={0}
                    placeholder='e.g. 3'
                    value={studentMinAge}
                    onChange={(e) => {
                      const val = e.target.value;
                      onStudentMinAgeChange(
                        val === '' ? '' : Math.max(0, Number(val)),
                      );
                    }}
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Max Age (Optional)</Label>
                  <Input
                    type='number'
                    min={0}
                    placeholder='e.g. 10'
                    value={studentMaxAge}
                    onChange={(e) => {
                      const val = e.target.value;
                      onStudentMaxAgeChange(
                        val === '' ? '' : Math.max(0, Number(val)),
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {report.hasClassKeywordsInput && (
            <div className='space-y-2'>
              <Label>Class Name Contains (Optional)</Label>
              <Input
                placeholder='e.g. Pointe, Contemporary'
                value={classKeywords}
                onChange={(e) => {
                  onClassKeywordsChange(e.target.value);
                }}
              />
              <p className='text-xs text-muted-foreground'>
                Comma-separated keywords. Leave blank to include all students in
                the session.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='secondary'
            onClick={onDownload}
            disabled={isDownloadDisabled()}
          >
            {isLoading ? <Loader2 className='animate-spin' /> : <Download />}
            Download Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
